import { useEffect, useRef, useCallback } from 'react';
import { useSession, signOut } from 'next-auth/react';

/**
 * Hook to monitor session validity and detect if session is invalidated
 * Automatically logs out the user if their session is invalidated from another device
 * Works across subdomains via HTTP cookies
 */
export function useSessionValidator() {
  const { data: session, status } = useSession();
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);
  const hasSignedOutRef = useRef(false);

  // Validate session periodically
  const validateSession = useCallback(async () => {
    if (!session?.user?.email) return;
    if (isCheckingRef.current || hasSignedOutRef.current) return;

    isCheckingRef.current = true;

    try {
      const response = await fetch('/api/auth/session/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // If endpoint not accessible, skip validation
        console.warn('Session validation endpoint returned status:', response.status);
        return;
      }

      const data = await response.json();

      if (data.isValid === false) {
        // Session was invalidated (for example from another device login)
        console.log('Session invalidated:', data.reason);
        hasSignedOutRef.current = true;
        localStorage.setItem('session_invalidated', 'true');
        const callbackUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login';
        await signOut({ redirect: true, callbackUrl });
      }
    } catch (error) {
      console.warn('Session validation warning (non-critical):', error);
      // Don't let validation errors prevent normal operation
    } finally {
      isCheckingRef.current = false;
    }
  }, [session]);

  // Set up periodic validation
  useEffect(() => {
    if (status === 'authenticated') {
      // Validate quickly so remote-session takeovers are enforced without waiting for refresh.
      validateSession();

      // Poll frequently to reduce the window where an invalidated session can still be used.
      checkIntervalRef.current = setInterval(validateSession, 20 * 1000);

      const handleVisibilityOrFocus = () => {
        if (document.visibilityState === 'visible') {
          validateSession();
        }
      };

      window.addEventListener('focus', handleVisibilityOrFocus);
      document.addEventListener('visibilitychange', handleVisibilityOrFocus);

      return () => {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
        }
        window.removeEventListener('focus', handleVisibilityOrFocus);
        document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      };
    }

    hasSignedOutRef.current = false;
  }, [status, validateSession]);

  return { validateSession };
}

/**
 * Hook to listen for session invalidation across tabs/windows
 * Detects when session is invalidated from another tab and logs out user
 */
export function useStorageSessionListener() {
  const { status } = useSession();

  useEffect(() => {
    if (status !== 'authenticated') return;

    // Use BroadcastChannel for cross-tab communication (if supported)
    let channel: BroadcastChannel | null = null;

    try {
      channel = new BroadcastChannel('auth');
      channel.onmessage = (event) => {
        if (event.data.type === 'logout') {
          console.log('Session invalidated from another tab');
          const callbackUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login';
          signOut({ redirect: true, callbackUrl });
        }
      };
    } catch (e) {
      // BroadcastChannel not supported, fall back to storage events
      const handleStorageChange = async (e: StorageEvent) => {
        // If session was invalidated from another tab
        if (e.key === 'session_invalidated' && e.newValue === 'true') {
          console.log('Session invalidated from another tab');
          localStorage.removeItem('session_invalidated');
          const callbackUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login';
          await signOut({ redirect: true, callbackUrl });
        }
      };

      window.addEventListener('storage', handleStorageChange);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }

    return () => {
      if (channel) {
        channel.close();
      }
    };
  }, [status]);
}
